/**
 * Flow Hub Choreography — verbindliche Design-Tokens für den Wochenplan-Redesign.
 *
 * Alle Komponenten in src/components/FlowHub/* konsumieren ausschliesslich
 * Tokens aus dieser Datei. Inline-Animations-Werte sind verboten — wer eine
 * neue Bewegung braucht, ergänzt zuerst einen Token hier.
 *
 * Disziplin sichert: kohärente Bewegung, einfache Reduktion bei prefers-reduced-motion,
 * einheitliche Tuning-Punkte für alle Tile-/Stage-Übergänge.
 */

import type { Transition } from 'framer-motion';
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import {
  BookmarkSimple,
  BookOpen,
  Calculator,
  Cpu,
  Globe,
  PersonSimpleRun,
  Translate,
  MusicNote,
  Palette,
} from '@phosphor-icons/react/dist/ssr';

// ============================================================================
// Spring-Configs
// ============================================================================

/** Aurora-Pulse, Subject-Glow, langsame ambient Effekte. Weich. */
export const SPRING_AURORA_PULSE: Transition = {
  type: 'spring',
  stiffness: 120,
  damping: 18,
  mass: 1,
};

/** Glow-Landing nach Reflex-Tap. Schnell und entschieden. */
export const SPRING_GLOW_LANDING: Transition = {
  type: 'spring',
  stiffness: 320,
  damping: 24,
  mass: 0.8,
};

// ============================================================================
// Premium Shadow Stacks
//
// Mehrschichtige Schatten für „echte" Tiefe statt einer Neon-Border.
// Aufbau (von oben nach unten):
//   1. inset top-highlight (Glas-Glanz)
//   2. close ambient (1–2 px, gegen flat)
//   3. medium ambient (10–16 px, Hover/Resting)
//   4. far accent-glow (24–60 px, farbiger Aurora-Halo)
//
// Verbrauch via Template:  premiumShadow({ aurora, accent, ambient })
// ============================================================================

export interface PremiumShadowOptions {
  /** Akzentfarbe für den weiten Glow (rgba). */
  accentGlow: string;
  /** Stärke des Akzent-Halos: 'soft' | 'medium' | 'strong' */
  accentStrength?: 'soft' | 'medium' | 'strong';
  /** Inset-Top-Highlight (Glas-Glanz). Default: light. */
  highlight?: 'light' | 'dark' | 'none';
}

export function premiumShadow(opts: PremiumShadowOptions): string {
  const strength = opts.accentStrength ?? 'medium';
  const farRadius = strength === 'soft' ? 28 : strength === 'strong' ? 72 : 48;
  const farOpacity = strength === 'soft' ? '0.6' : strength === 'strong' ? '1' : '0.85';

  const highlight =
    opts.highlight === 'none'
      ? ''
      : opts.highlight === 'dark'
        ? 'inset 0 1px 0 rgba(255,255,255,0.08), '
        : 'inset 0 1px 0 rgba(255,255,255,0.85), ';

  // Akzent-Color mit Strength-Modifikator:
  //   Wir nehmen den raw rgba-String und multiplizieren die Alpha-Komponente.
  //   Da accentGlow als `rgba(..., x)` reinkommt, lassen wir Alpha unverändert
  //   und nutzen farOpacity als zusätzlichen Layer-Modifier über CSS mix? Nein —
  //   wir geben einfach den raw-String weiter, plus ein zweiten weichen Layer.
  const accent = opts.accentGlow;

  return (
    `${highlight}` +
    `0 1px 2px rgba(15,23,42,0.04), ` +
    `0 8px 20px rgba(15,23,42,0.06), ` +
    `0 ${farRadius}px ${farRadius * 1.6}px ${accent}` +
    (strength === 'strong' ? `, 0 0 0 1px ${accent}` : '') +
    `, /* far ambient ${farOpacity} */ 0 0 0 0 transparent`
  );
}

// ============================================================================
// Aurora-Glow-Farben pro Fach
//
// Soft-Werte sind die rgba-Glows für box-shadow (Card-Aura).
// Hex-Werte sind die Akzente für Borders / Section-Aurora-Linien.
// Korreliert mit SUBJECT_COLORS aus src/lib/constants.ts (border-l-*-400).
// ============================================================================

export interface AuroraTone {
  hex: string;
  soft: string;
}

export const SUBJECT_AURORA: Record<string, AuroraTone> = {
  'Mathematik':              { hex: '#60a5fa', soft: 'rgba(96, 165, 250, 0.25)' },
  'Deutsch':                 { hex: '#fb923c', soft: 'rgba(251, 146, 60, 0.25)' },
  'NMG':                     { hex: '#34d399', soft: 'rgba(52, 211, 153, 0.25)' },
  'Englisch':                { hex: '#c084fc', soft: 'rgba(192, 132, 252, 0.25)' },
  'Französisch':             { hex: '#fb7185', soft: 'rgba(251, 113, 133, 0.25)' },
  'Bildnerisches Gestalten': { hex: '#f472b6', soft: 'rgba(244, 114, 182, 0.25)' },
  'Musik':                   { hex: '#a78bfa', soft: 'rgba(167, 139, 250, 0.25)' },
  'Medien & Informatik':     { hex: '#22d3ee', soft: 'rgba(34, 211, 238, 0.25)' },
  'Bewegung & Sport':        { hex: '#a3e635', soft: 'rgba(163, 230, 53, 0.25)' },
  'Allgemein':               { hex: '#94a3b8', soft: 'rgba(148, 163, 184, 0.25)' },
};

export const DEFAULT_AURORA: AuroraTone = {
  hex: '#94a3b8',
  soft: 'rgba(148, 163, 184, 0.25)',
};

export function auroraFor(subject: string): AuroraTone {
  return SUBJECT_AURORA[subject] ?? DEFAULT_AURORA;
}

// ============================================================================
// Subject-Ton (Planseite «Arbeitsplatz»): kräftige Tinte für Glyph-Flächen und
// Schrift, zarte Fläche für Kartenköpfe. Gleiche Farbfamilien wie
// SUBJECT_COLORS (Tailwind 700 / 100); weisse Schrift auf der Tinte hält AA.
// ============================================================================

export interface SubjectTon {
  ink: string;
  soft: string;
}

export const SUBJECT_TON: Record<string, SubjectTon> = {
  'Mathematik':              { ink: '#1d4ed8', soft: '#dbeafe' },
  'Deutsch':                 { ink: '#c2410c', soft: '#ffedd5' },
  'NMG':                     { ink: '#047857', soft: '#d1fae5' },
  'Englisch':                { ink: '#7e22ce', soft: '#f3e8ff' },
  'Französisch':             { ink: '#be123c', soft: '#ffe4e6' },
  'Bildnerisches Gestalten': { ink: '#be185d', soft: '#fce7f3' },
  'Musik':                   { ink: '#6d28d9', soft: '#ede9fe' },
  'Medien & Informatik':     { ink: '#0e7490', soft: '#cffafe' },
  'Bewegung & Sport':        { ink: '#4d7c0f', soft: '#ecfccb' },
  'Allgemein':               { ink: '#334155', soft: '#f1f5f9' },
};

export const DEFAULT_TON: SubjectTon = { ink: '#334155', soft: '#f1f5f9' };

export function subjectTonFor(subject: string): SubjectTon {
  return SUBJECT_TON[subject] ?? DEFAULT_TON;
}

// ============================================================================
// Subject-Tint (Tailwind-Gradient-Klassen pro Fach für TaskTile-Background)
//
// Light- und Dark-Mode getrennt, damit Tailwind v4 die Klassen statisch
// extrahieren kann (keine dynamischen `bg-${color}-50`-Strings). Konsumenten
// kombinieren `bg-gradient-to-br ${SUBJECT_TINT_LIGHT[s]} ${SUBJECT_TINT_DARK[s]}`.
// Korreliert farblich mit SUBJECT_AURORA und SUBJECT_COLORS in src/lib/constants.ts.
// ============================================================================

export const SUBJECT_TINT_LIGHT: Record<string, string> = {
  'Mathematik':              'from-blue-100 via-blue-50 to-white',
  'Deutsch':                 'from-orange-100 via-orange-50 to-white',
  'NMG':                     'from-emerald-100 via-emerald-50 to-white',
  'Englisch':                'from-purple-100 via-purple-50 to-white',
  'Französisch':             'from-rose-100 via-rose-50 to-white',
  'Bildnerisches Gestalten': 'from-pink-100 via-pink-50 to-white',
  'Musik':                   'from-violet-100 via-violet-50 to-white',
  'Medien & Informatik':     'from-cyan-100 via-cyan-50 to-white',
  'Bewegung & Sport':        'from-lime-100 via-lime-50 to-white',
  'Allgemein':               'from-slate-100 via-slate-50 to-white',
};

export const SUBJECT_TINT_DARK: Record<string, string> = {
  'Mathematik':              'dark:from-blue-950/40 dark:via-slate-800 dark:to-slate-800',
  'Deutsch':                 'dark:from-orange-950/40 dark:via-slate-800 dark:to-slate-800',
  'NMG':                     'dark:from-emerald-950/40 dark:via-slate-800 dark:to-slate-800',
  'Englisch':                'dark:from-purple-950/40 dark:via-slate-800 dark:to-slate-800',
  'Französisch':             'dark:from-rose-950/40 dark:via-slate-800 dark:to-slate-800',
  'Bildnerisches Gestalten': 'dark:from-pink-950/40 dark:via-slate-800 dark:to-slate-800',
  'Musik':                   'dark:from-violet-950/40 dark:via-slate-800 dark:to-slate-800',
  'Medien & Informatik':     'dark:from-cyan-950/40 dark:via-slate-800 dark:to-slate-800',
  'Bewegung & Sport':        'dark:from-lime-950/40 dark:via-slate-800 dark:to-slate-800',
  'Allgemein':               'dark:from-slate-700/30 dark:via-slate-800 dark:to-slate-800',
};

export const DEFAULT_TINT_LIGHT = 'from-slate-100 via-slate-50 to-white';
export const DEFAULT_TINT_DARK = 'dark:from-slate-700/30 dark:via-slate-800 dark:to-slate-800';

export function subjectTintFor(subject: string): { light: string; dark: string } {
  return {
    light: SUBJECT_TINT_LIGHT[subject] ?? DEFAULT_TINT_LIGHT,
    dark: SUBJECT_TINT_DARK[subject] ?? DEFAULT_TINT_DARK,
  };
}

// ============================================================================
// Subject-Glyph (Lucide-Icon pro Fach für TaskTile-Badge)
//
// Wird im 40×40px-Glyph-Badge oben links der TaskTile gerendert. Die Emoji-Icons
// in `src/lib/constants.ts` (SUBJECT_COLORS) bleiben für den Track-Header reserviert.
// ============================================================================

export const SUBJECT_GLYPH: Record<string, PhosphorIcon> = {
  'Mathematik':              Calculator,
  'Deutsch':                 BookOpen,
  'NMG':                     Globe,
  'Englisch':                Translate,
  'Französisch':             Translate,
  'Bildnerisches Gestalten': Palette,
  'Musik':                   MusicNote,
  'Medien & Informatik':     Cpu,
  'Bewegung & Sport':        PersonSimpleRun,
  'Allgemein':               BookmarkSimple,
};

export const DEFAULT_GLYPH: PhosphorIcon = BookmarkSimple;

export function subjectGlyphFor(subject: string): PhosphorIcon {
  return SUBJECT_GLYPH[subject] ?? DEFAULT_GLYPH;
}

// ============================================================================
// Sweep-Delay (deterministisch pro Task-ID)
//
// Verhindert synchrone Sweep-Animationen über Hero + Tiles. Buckets aus
// `[-0s, -3s, -6s, -9s]` versetzen den 12s-Cycle viertel-aware. Ergebnis: über
// das gesamte Plan-Tab verteilt sieht man ~1 Sweep alle 3-4 s, pro Karte aber
// nur alle 12 s. Deterministisch, damit Re-Renders nicht hin- und herspringen.
// ============================================================================

export const SWEEP_DELAY_BUCKETS = ['0s', '-3s', '-6s', '-9s'] as const;

export function sweepDelayFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = ((h * 31) + id.charCodeAt(i)) | 0;
  }
  const bucket = Math.abs(h) % SWEEP_DELAY_BUCKETS.length;
  return SWEEP_DELAY_BUCKETS[bucket] ?? SWEEP_DELAY_BUCKETS[0];
}

// ============================================================================
// Status-Glow-Tokens (TaskTile-Skins)
//
// status-spezifische Glow-Stärke, unabhängig vom Fach.
// ============================================================================

export const STATUS_GLOW = {
  null:    { intensity: 0.0,  ring: 'rgba(99, 102, 241, 0.0)',  shadow: 'transparent' },
  active:  { intensity: 0.6,  ring: 'rgba(245, 158, 11, 0.55)', shadow: '0 0 28px rgba(245, 158, 11, 0.35), 0 0 0 2px rgba(245, 158, 11, 0.4)' },
  paused:  { intensity: 0.3,  ring: 'rgba(148, 163, 184, 0.5)', shadow: '0 0 18px rgba(148, 163, 184, 0.25)' },
  muted:   { intensity: 0.0,  ring: 'rgba(148, 163, 184, 0.2)', shadow: 'none' },
  help:    { intensity: 0.5,  ring: 'rgba(244, 63, 94, 0.5)',   shadow: '0 0 24px rgba(244, 63, 94, 0.3)' },
  done:    { intensity: 0.4,  ring: 'rgba(16, 185, 129, 0.45)', shadow: '0 0 22px rgba(16, 185, 129, 0.28)' },
  recommended: { intensity: 0.7, ring: 'rgba(99, 102, 241, 0.6)', shadow: '0 0 32px rgba(99, 102, 241, 0.4), 0 0 0 2px rgba(99, 102, 241, 0.5)' },
} as const;

// ============================================================================
// View-Transitions-API Wrapper (mit Framer-Fallback)
//
// Nutzung:
//   await runWithViewTransition(() => setSelectedTaskId(taskId));
// Wenn die API verfügbar ist, läuft die Mutation in einer nativen
// View-Transition. Sonst sofort. In beiden Fällen übernimmt Framer-Motion
// die Tile-zu-Stage-Animation via layoutId.
// ============================================================================

export async function runWithViewTransition(mutator: () => void | Promise<void>): Promise<void> {
  const doc = typeof document !== 'undefined' ? document : null;
  if (doc && 'startViewTransition' in doc && typeof (doc as Document & { startViewTransition?: (cb: () => void | Promise<void>) => { finished: Promise<void> } }).startViewTransition === 'function') {
    const transition = (doc as Document & { startViewTransition: (cb: () => void | Promise<void>) => { finished: Promise<void> } }).startViewTransition(async () => {
      await mutator();
    });
    await transition.finished.catch(() => {
      // View-Transition kann abbrechen (z.B. neuer Navigation) — kein Drama.
    });
    return;
  }
  await mutator();
}

// ============================================================================
// Haptik-Helper (Mobile/iPad Vibration API)
// ============================================================================

export type HapticIntensity = 'micro' | 'soft' | 'celebrate';

const HAPTIC_PATTERNS: Record<HapticIntensity, number | number[]> = {
  micro: 8,        // Reflex-Smiley-Tap
  soft: 12,        // Lock-Confirm-Open
  celebrate: [20, 60, 20], // Combo-Reach 3/5/7
};

/**
 * Löst eine sehr kurze, klassenraum-taugliche Vibration aus.
 * Skipped silent, wenn API nicht verfügbar oder global/persönlich deaktiviert.
 *
 * @param intensity Vorgegebenes Muster
 * @param enabledChecks Beide müssen true sein, damit Haptik feuert
 */
export function fireHaptic(
  intensity: HapticIntensity,
  enabledChecks: { perStudent: boolean; perClass: boolean } = { perStudent: true, perClass: true },
): void {
  if (!enabledChecks.perStudent || !enabledChecks.perClass) return;
  if (typeof navigator === 'undefined' || !('vibrate' in navigator)) return;
  try {
    navigator.vibrate(HAPTIC_PATTERNS[intensity]);
  } catch {
    // iOS Safari oder reduzierte Berechtigungen — silent skip.
  }
}

// ============================================================================
// Combo-Konfiguration
// ============================================================================

export const COMBO = {
  /** Combo bricht, wenn keine Aufgabe in dieser Zeit erledigt wird. */
  idleBreakMs: 5 * 60 * 1000,
  /** Schwellen für intensivere Aurora-Pulses. */
  thresholds: [3, 5, 7] as const,
} as const;

// SMART_HINT_POOL und die Smart-Hint-Heuristik leben in `src/lib/flowHubRecommendation.ts`,
// damit Server-Code (Lib) sie nutzen kann, ohne die Client-Helper (runWithViewTransition,
// fireHaptic) mitzuziehen.
